/* =========================================================================
   dn-single-open.js  —  DeskNeko 4.2 (Claude rebuild)

   Progressive enhancement that enforces single-open (accordion) behaviour:
   opening one item in a GROUP collapses the others in that same group.

   Two mechanisms, matching the two disclosure families in the theme:

   1. Native <details> / <summary>
      On `toggle` (capturing, since it does not bubble), when a <details>
      opens we close its open siblings in the same group. A group is the
      nearest [data-dn-accordion-group] / [data-dn-faq-group] ancestor, or
      failing that the element's own parent (a container holding sibling
      <details>). This gives per-topic single-open in grouped FAQs
      (dn-help-faq) and whole-list single-open in flat lists (dn-custom-faq).

   2. Bootstrap collapses (.accordion + .accordion-collapse)
      Real theme accordions already declare data-bs-parent, so Bootstrap
      itself enforces single-open there — we detect that and stand down.
      As a safety net, any .accordion whose collapses LACK data-bs-parent
      gets a `show.bs.collapse` handler that closes the group's other open
      panels.

   Deliberately excluded (kept multi-open / untouched): navigation and the
   mobile off-canvas menu (usability), collection filters, cart, and anything
   opted out with [data-dn-multi] or [data-dn-single-open="off"].

   Safe by design: idempotent (no double-binding), no library required for
   the native path, ARIA left to the platform (native <details> and
   Bootstrap manage aria-expanded themselves), reduced-motion friendly
   (native toggles are instant; collapse animation is governed by CSS).
   ========================================================================= */
(function () {
  'use strict';

  if (window.__dnSingleOpen) return;
  window.__dnSingleOpen = true;

  /* Contexts where single-open would hurt (menus, filters) or is opted out. */
  var EXCLUDE =
    'nav, .navbar, .offcanvas-menu, #offcanvas-menu, #offcanvas-categories, ' +
    '.dn-filter-test, [data-dn-filter-group], ' +
    '.cart, #offcanvas-cart, [data-dn-multi], [data-dn-single-open="off"], ' +
    '.dn-no-single-open';

  function isExcluded(el) {
    return !!(el && el.closest && el.closest(EXCLUDE));
  }

  /* -----------------------------------------------------------------------
     1. Native <details> single-open
     --------------------------------------------------------------------- */
  function detailsGroup(d) {
    var explicit = d.closest('[data-dn-accordion-group], [data-dn-faq-group]');
    return explicit || d.parentElement;
  }

  function handleDetailsToggle(event) {
    var opened = event.target;
    if (!opened || opened.tagName !== 'DETAILS' || !opened.open) return;
    if (isExcluded(opened)) return;

    var group = detailsGroup(opened);
    if (!group) return;

    var candidates = group.getElementsByTagName('details');
    for (var i = 0; i < candidates.length; i++) {
      var other = candidates[i];
      if (other === opened || !other.open) continue;
      // Respect nested sub-groups: only close items whose own group matches.
      if (detailsGroup(other) !== group) continue;
      if (isExcluded(other)) continue;
      other.open = false; // fires its own toggle (open=false) — ignored above
    }
  }

  // `toggle` does not bubble, so listen in the capture phase on the document.
  document.addEventListener('toggle', handleDetailsToggle, true);

  /* -----------------------------------------------------------------------
     2. Bootstrap collapse single-open (safety net for accordions that lack
        a shared data-bs-parent)
     --------------------------------------------------------------------- */
  function initBootstrapAccordion(accordion) {
    if (!accordion || accordion.__dnSingleOpen) return;
    if (isExcluded(accordion)) return;

    var collapses = accordion.querySelectorAll('.accordion-collapse');
    if (collapses.length < 2) {
      accordion.__dnSingleOpen = true;
      return;
    }

    // If Bootstrap already governs this group via data-bs-parent, stand down.
    var hasParent = false;
    for (var i = 0; i < collapses.length; i++) {
      if (collapses[i].hasAttribute('data-bs-parent')) {
        hasParent = true;
        break;
      }
    }
    accordion.__dnSingleOpen = true;
    if (hasParent) return;

    accordion.addEventListener('show.bs.collapse', function (event) {
      var opened = event.target;
      if (!opened || !opened.classList.contains('accordion-collapse')) return;
      // Only the collapses that belong directly to THIS accordion.
      if (opened.closest('.accordion') !== accordion) return;

      var panels = accordion.querySelectorAll('.accordion-collapse.show');
      for (var j = 0; j < panels.length; j++) {
        var panel = panels[j];
        if (panel === opened) continue;
        if (panel.closest('.accordion') !== accordion) continue;
        if (window.bootstrap && window.bootstrap.Collapse) {
          window.bootstrap.Collapse.getOrCreateInstance(panel).hide();
        } else {
          panel.classList.remove('show');
        }
      }
    });
  }

  function initAllBootstrap(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var accordions = scope.querySelectorAll('.accordion');
    for (var i = 0; i < accordions.length; i++) {
      initBootstrapAccordion(accordions[i]);
    }
  }

  /* -----------------------------------------------------------------------
     Boot + theme-editor re-init. (Native <details> need no re-init — the
     document-level capture listener also catches dynamically added ones.)
     --------------------------------------------------------------------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAllBootstrap(document);
    });
  } else {
    initAllBootstrap(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAllBootstrap(event.target || document);
  });
})();
